import { getChatApiBase, getChatApiHeaders } from '../chatApiClient'

/**
 * Shared request plumbing and wire types for every typed agent service, so the sibling
 * folders cannot drift apart on host, headers, pagination or error shape.
 */

/** The `apps.active` fields these calls need; `meta` is left open, it carries more. */
export interface ChatApp {
  id?: string
  appRegion?: {
    meta?: { apiHost?: string; [key: string]: unknown }
    [key: string]: unknown
  }
  [key: string]: unknown
}

/** Chat-API error envelope: `{ error: { source, code, message, devMessage } }`. */
export interface ChatApiErrorBody {
  source?: string
  code?: string
  message?: string
  devMessage?: string
  details?: string[]
}

/**
 * Keyset cursor. The API sends an empty ARRAY — not `null`, not `{}` — when there is no
 * page in that direction, so presence is an `Array.isArray` test.
 */
export type AgentCursor = { affix: 'append' | 'prepend'; createdAt: number } | []

/**
 * List `meta` across the agent surface. `previous`/`next` are optional because the
 * knowledge-base endpoint omits them entirely while its siblings send `[]`.
 */
export interface ListMeta {
  current: { limit: number; count: number }
  previous?: AgentCursor
  next?: AgentCursor
}

const DEFAULT_API_HOST = 'api.cometchat.com'

/** Only consulted in direct mode; the mgmt-api wrapper ignores the region host. */
export const resolveApiHost = (app: ChatApp): string =>
  app?.appRegion?.meta?.apiHost || DEFAULT_API_HOST

/** chat-api base up to and including the version segment (e.g. `…/v3.0`). */
export const apiBase = (app: ChatApp): string => getChatApiBase(app, resolveApiHost(app))

export const agentApiHeaders = (app: ChatApp): Record<string, string> => ({
  ...getChatApiHeaders(app),
  chatapiversion: 'v3.0',
})

/** Joins the parts that are actually set, preserving the order given. */
export const withQuery = (url: string, params: Array<string | undefined>): string => {
  const query = params.filter(Boolean).join('&')
  return query ? `${url}?${query}` : url
}

/** `page`/`search` go through URLSearchParams, so the term is never interpolated raw. */
export const pageParams = (page?: number, search?: string): string => {
  const params = new URLSearchParams()
  if (page) params.append('page', String(page))
  if (search) params.append('search', search)
  return params.toString()
}

/**
 * A cursor is present only as a populated object. The API sends `[]` for "no page this
 * way", the knowledge-base `meta` omits the key entirely, and redux state that has been
 * cleared holds `{}` — so all four shapes have to read as absent.
 */
export const hasCursor = (cursor?: AgentCursor | Record<string, unknown>): boolean => {
  if (!cursor || typeof cursor !== 'object') return false
  if (Array.isArray(cursor)) return false
  return Object.keys(cursor).length > 0
}

/** Empty string when the cursor is absent, so `withQuery` drops the parameter. */
export const cursorParams = (cursor?: AgentCursor): string => {
  if (!hasCursor(cursor)) return ''
  const { affix, createdAt } = cursor as { affix: string; createdAt: number }
  return `affix=${encodeURIComponent(affix)}&createdAt=${createdAt}`
}

interface ChatApiAxiosError extends Error {
  response?: { data?: { error?: ChatApiErrorBody } }
}

/** The API prefixes validation messages with a full stop (`.name should not be empty`). */
const stripLeadingDot = (message: string): string => message.replace(/^\.+/, '')

/**
 * Cleans the leading full stop off the API's validation messages and mirrors the result onto
 * the thrown error's own `message`, so a consumer can surface `error.message` unedited.
 */
export const normalizeChatApiError = (error: unknown): unknown => {
  const apiError = (error as ChatApiAxiosError)?.response?.data?.error
  if (!apiError) return error

  if (typeof apiError.message === 'string') apiError.message = stripLeadingDot(apiError.message)
  if (typeof apiError.devMessage === 'string') {
    apiError.devMessage = stripLeadingDot(apiError.devMessage)
  }
  if (error instanceof Error && apiError.message) error.message = apiError.message

  return error
}

/** Every agent call REJECTS on failure — callers branch in `catch`, never on a resolved error. */
export async function chatApiRequest<T>(send: () => Promise<{ data: T }>): Promise<T> {
  try {
    const response = await send()
    return response.data
  } catch (error) {
    throw normalizeChatApiError(error)
  }
}
